/**
 * Pre-send checks for compose (email / notice) before calling SOAP `sendMessage`.
 *
 * **Prod behavior (unchanged):**
 * - Notify users require a non-folder context (project or activity), because
 *   `resolvedIsFolderContext === true` means folder-only / default inbox node.
 * - Comment/resolve flows (`requiresExplicitContextSelection`) also block send on
 *   folder-only context until the user picks project/activity.
 *
 * Centralized here so Communication compose and global NewMessageDialog stay aligned
 * and changes are made in one place.
 */
export type ComposeSendPreflightFailure =
  | "notify_requires_project_or_activity"
  | "explicit_context_before_send";

export function getComposeSendPreflightFailure(params: {
  hasNotifyUsers: boolean;
  resolvedIsFolderContext: boolean;
  /** Current compose message type (e.g. "7" = Email). */
  messageType?: string | number | null;
  /** True for forced internal message type "1" (comment/resolve) when target has no context on the message object. */
  requiresExplicitContextSelection?: boolean;
}): ComposeSendPreflightFailure | null {
  const {
    hasNotifyUsers,
    resolvedIsFolderContext,
    messageType,
    requiresExplicitContextSelection = false,
  } = params;

  const normalizedType = messageType != null ? String(messageType) : "";
  const isEmail = normalizedType === "7";

  // Legacy/Lagoon behavior: Email notify works from folder-backed lists (inbox/todo)
  // and does not require project/activity context selection.
  if (!isEmail && hasNotifyUsers && resolvedIsFolderContext) {
    return "notify_requires_project_or_activity";
  }
  if (requiresExplicitContextSelection && resolvedIsFolderContext) {
    return "explicit_context_before_send";
  }
  return null;
}

export function formStateHasNotifyUsers(formState: { notify?: unknown } | null | undefined): boolean {
  const n = formState?.notify;
  return Array.isArray(n) && (n.length ?? 0) > 0;
}
